import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Renderer2,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ButtonModule } from 'primeng/button';
import { Dialog, DialogModule } from 'primeng/dialog';

import {
  DiscardChangesDialogHandle,
  DiscardChangesDialogService,
} from '@shared/components/overlay/discard-changes-dialog/discard-changes-dialog.service';

import { FormModalShellSize } from './form-modal-shell.models';

const CONTENT_RESIZE_TRANSITION_FALLBACK_BUFFER_MS = 80;

function resolveDialogWidth(size: FormModalShellSize): string {
  switch (size) {
    case 'small':
      return '32rem';
    case 'large':
      return '64rem';
    case 'medium':
    default:
      return '48rem';
  }
}

@Component({
  selector: 'app-form-modal-shell',
  standalone: true,
  imports: [DialogModule, ButtonModule],
  templateUrl: './form-modal-shell.component.html',
  styleUrl: './form-modal-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormModalShellComponent {
  visible = input(false);
  heading = input('Edycja');
  subtitle = input<string | null>(null);
  size = input<FormModalShellSize>('medium');
  dialogWidth = input<string | null>(null);
  saveLabel = input('Zapisz');
  cancelLabel = input('Anuluj');
  submitFormId = input<string | null>(null);
  saving = input(false);
  submitDisabled = input(false);
  confirmOnDirtyExit = input(false);
  hasPendingChanges = input(false);
  confirmDiscardMessage = input('Masz niezapisane zmiany. Czy na pewno chcesz je odrzucić?');
  animateContentResize = input(true);

  visibleChange = output<boolean>();
  hidden = output<void>();
  saveRequested = output<void>();
  readonly mainDialog = viewChild<Dialog>('mainDialog');
  readonly shellContainer = viewChild<ElementRef<HTMLElement>>('shellContainer');

  readonly dialogVisibleState = signal(false);
  readonly savingBusy = computed(() => this.saving());
  readonly interactionLocked = computed(() => this.saving());
  readonly dialogStyle = computed(() => ({
    width: this.dialogWidth() ?? resolveDialogWidth(this.size()),
    maxWidth: 'calc(100vw - 2rem)',
    maxHeight: 'calc(100vh - 2rem)',
  }));
  readonly dialogStyleClass = 'app-modal--form app-form-modal-shell-dialog';
  readonly discardConfirmPending = signal(false);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly discardChangesDialogService = inject(DiscardChangesDialogService);
  private mainDialogMaskMouseDownUnlistener: (() => void) | null = null;
  private mainDialogEscapeUnlistener: (() => void) | null = null;
  private shellTransitionEndUnlistener: (() => void) | null = null;
  private shellResizeObserver: ResizeObserver | null = null;
  private observedShellElement: HTMLElement | null = null;
  private shellResizeTransitionFallback: number | null = null;
  private lastObservedShellHeight: number | null = null;
  private shellResizeAnimationActive = false;
  private contentResizeAnimationArmed = false;
  private discardConfirmHandle: DiscardChangesDialogHandle | null = null;
  private readonly syncVisibleEffect = effect(() => {
    if (!this.visible()) {
      this.closeDiscardConfirm();
      this.dialogVisibleState.set(false);
      return;
    }

    this.dialogVisibleState.set(true);
  });
  private readonly contentResizeAnimationEffect = effect(() => {
    const shell = this.shellContainer()?.nativeElement;
    if (!this.animateContentResize() || !this.dialogVisibleState() || !shell) {
      this.unbindContentResizeAnimation();
      return;
    }

    this.bindContentResizeAnimation(shell);
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.unbindMainDialogCloseIntentListeners();
      this.unbindContentResizeAnimation();
      this.closeDiscardConfirm();
    });
  }

  onSaveRequested(): void {
    if (this.interactionLocked() || this.submitDisabled()) {
      return;
    }

    this.saveRequested.emit();
  }

  onCancelButtonClick(): void {
    this.requestClose();
  }

  onHeaderCloseButtonClick(): void {
    this.requestClose();
  }

  onMainDialogShown(): void {
    this.bindMainDialogCloseIntentListeners();
  }

  onDialogHidden(): void {
    this.unbindMainDialogCloseIntentListeners();
    if (!this.dialogVisibleState()) {
      this.hidden.emit();
    }
  }

  onMainDialogMaskMouseDown(event: MouseEvent): void {
    const dialog = this.mainDialog();
    const wrapper = dialog?.wrapper;
    if (!wrapper || !this.shouldHandleMainDialogCloseIntent() || event.target !== wrapper) {
      return;
    }

    event.preventDefault();
    this.requestClose();
  }

  onMainDialogEscapeKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this.shouldHandleMainDialogCloseIntent()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.requestClose();
  }

  private requestClose(): void {
    if (this.interactionLocked()) {
      this.dialogVisibleState.set(true);
      return;
    }

    if (this.discardConfirmPending()) {
      return;
    }

    if (this.confirmOnDirtyExit() && this.hasPendingChanges()) {
      this.openDiscardConfirm();
      return;
    }

    this.dialogVisibleState.set(false);
    this.visibleChange.emit(false);
  }

  private bindMainDialogCloseIntentListeners(): void {
    const wrapper = this.mainDialog()?.wrapper;
    if (!wrapper) {
      return;
    }

    this.unbindMainDialogCloseIntentListeners();
    this.mainDialogMaskMouseDownUnlistener = this.renderer.listen(wrapper, 'mousedown', (event: MouseEvent) => {
      this.onMainDialogMaskMouseDown(event);
    });
    this.mainDialogEscapeUnlistener = this.renderer.listen(this.document, 'keydown', (event: KeyboardEvent) => {
      this.onMainDialogEscapeKey(event);
    });
  }

  private unbindMainDialogCloseIntentListeners(): void {
    this.mainDialogMaskMouseDownUnlistener?.();
    this.mainDialogMaskMouseDownUnlistener = null;
    this.mainDialogEscapeUnlistener?.();
    this.mainDialogEscapeUnlistener = null;
  }

  private bindContentResizeAnimation(shell: HTMLElement): void {
    if (this.observedShellElement === shell) {
      return;
    }

    this.unbindContentResizeAnimation();
    this.observedShellElement = shell;
    this.lastObservedShellHeight = Math.ceil(shell.getBoundingClientRect().height);
    this.contentResizeAnimationArmed = false;
    this.shellTransitionEndUnlistener = this.renderer.listen(shell, 'transitionend', (event: TransitionEvent) => {
      this.onShellResizeTransitionEnd(event);
    });

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.shellResizeObserver = new ResizeObserver(() => {
      this.scheduleContentResizeAnimation();
    });
    this.shellResizeObserver.observe(shell);
  }

  private unbindContentResizeAnimation(): void {
    this.shellResizeObserver?.disconnect();
    this.shellResizeObserver = null;
    this.observedShellElement = null;
    this.shellTransitionEndUnlistener?.();
    this.shellTransitionEndUnlistener = null;

    this.clearShellResizeTransitionFallback();
    this.lastObservedShellHeight = null;
    this.shellResizeAnimationActive = false;
    this.contentResizeAnimationArmed = false;
    const shell = this.shellContainer()?.nativeElement;
    if (shell) {
      shell.style.height = '';
    }
  }

  private scheduleContentResizeAnimation(): void {
    if (this.shellResizeAnimationActive) {
      return;
    }

    this.animateShellToCurrentHeight();
  }

  private animateShellToCurrentHeight(): void {
    const shell = this.observedShellElement;
    if (!shell || !this.animateContentResize() || !this.dialogVisibleState()) {
      return;
    }

    const nextHeight = Math.ceil(shell.getBoundingClientRect().height);
    const previousHeight = this.lastObservedShellHeight ?? nextHeight;
    this.lastObservedShellHeight = nextHeight;

    if (!this.contentResizeAnimationArmed) {
      this.contentResizeAnimationArmed = true;
      shell.style.height = '';
      return;
    }

    if (Math.abs(nextHeight - previousHeight) < 1) {
      return;
    }

    if (this.prefersReducedMotion()) {
      shell.style.height = '';
      return;
    }

    this.shellResizeAnimationActive = true;
    shell.style.height = `${previousHeight}px`;
    shell.getBoundingClientRect();
    shell.style.height = `${nextHeight}px`;
    this.startShellResizeTransitionFallback(shell);
  }

  private onShellResizeTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName !== 'height' || event.target !== this.observedShellElement) {
      return;
    }

    this.completeShellResizeAnimation();
  }

  private startShellResizeTransitionFallback(shell: HTMLElement): void {
    this.clearShellResizeTransitionFallback();
    this.shellResizeTransitionFallback = window.setTimeout(() => {
      this.shellResizeTransitionFallback = null;
      this.completeShellResizeAnimation();
    }, this.getShellHeightTransitionMs(shell) + CONTENT_RESIZE_TRANSITION_FALLBACK_BUFFER_MS);
  }

  private clearShellResizeTransitionFallback(): void {
    if (this.shellResizeTransitionFallback === null) {
      return;
    }

    clearTimeout(this.shellResizeTransitionFallback);
    this.shellResizeTransitionFallback = null;
  }

  private completeShellResizeAnimation(): void {
    const shell = this.observedShellElement;
    this.clearShellResizeTransitionFallback();
    this.shellResizeAnimationActive = false;
    if (!shell) {
      return;
    }

    shell.style.height = '';
    this.lastObservedShellHeight = Math.ceil(shell.getBoundingClientRect().height);
  }

  private prefersReducedMotion(): boolean {
    return this.document.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  private getShellHeightTransitionMs(shell: HTMLElement): number {
    const defaultView = this.document.defaultView;
    if (!defaultView) {
      return 0;
    }

    const styles = defaultView.getComputedStyle(shell);
    const properties = styles.transitionProperty.split(',').map((property) => property.trim());
    const durations = this.parseTransitionTimeList(styles.transitionDuration);
    const delays = this.parseTransitionTimeList(styles.transitionDelay);

    return properties.reduce((maxMs, property, index) => {
      if (property !== 'height' && property !== 'all') {
        return maxMs;
      }

      const duration = durations[index] ?? durations.at(-1) ?? 0;
      const delay = delays[index] ?? delays.at(-1) ?? 0;
      return Math.max(maxMs, duration + delay);
    }, 0);
  }

  private parseTransitionTimeList(value: string): number[] {
    return value.split(',').map((part) => {
      const trimmed = part.trim();
      if (trimmed.endsWith('ms')) {
        return Number.parseFloat(trimmed);
      }

      if (trimmed.endsWith('s')) {
        return Number.parseFloat(trimmed) * 1000;
      }

      return Number.parseFloat(trimmed) || 0;
    });
  }

  private shouldHandleMainDialogCloseIntent(): boolean {
    return this.dialogVisibleState() && !this.discardConfirmPending() && !this.interactionLocked();
  }

  private openDiscardConfirm(): void {
    this.discardConfirmPending.set(true);
    const handle = this.discardChangesDialogService.open({
      title: 'Odrzucić zmiany?',
      message: this.confirmDiscardMessage(),
      confirmLabel: 'Odrzuć zmiany',
      cancelLabel: 'Pozostań',
    });
    this.discardConfirmHandle = handle;

    handle.result$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((accepted) => {
        if (this.discardConfirmHandle !== handle) {
          return;
        }

        this.discardConfirmHandle = null;
        this.discardConfirmPending.set(false);

        if (!accepted) {
          return;
        }

        this.dialogVisibleState.set(false);
        this.visibleChange.emit(false);
      });
  }

  private closeDiscardConfirm(): void {
    const handle = this.discardConfirmHandle;
    this.discardConfirmHandle = null;
    this.discardConfirmPending.set(false);
    handle?.close(false);
  }
}
